import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../services/api';
import { Search, PackageCheck, AlertCircle, Filter, ArrowUpDown, ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import { io } from 'socket.io-client';
import { useSortableData } from '../hooks/useSortableData';
import Pagination from './Pagination';
import POMetrics from './POMetrics';

const POBinDetails = ({ sku, warehouse, poStatus, poUpdatedAt }) => {
    const [bins, setBins] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBins = async () => {
            try {
                const res = await api.inventory.getCycleCountBins(sku, warehouse);
                if (res?.data && Array.isArray(res.data)) {
                    let filteredBins = res.data;
                    
                    // If PO is closed/completed, only show "Received Locations" 
                    // (locations updated within 10 minutes of the PO completion)
                    if (poStatus === 'closed' && poUpdatedAt) {
                        const poTime = new Date(poUpdatedAt).getTime();
                        filteredBins = res.data.filter(bin => {
                            const binTime = new Date(bin.updated_at).getTime();
                            const diffMinutes = Math.abs(binTime - poTime) / (1000 * 60);
                            return diffMinutes <= 10; // 10 minute window
                        });
                    }
                    
                    setBins(filteredBins);
                }
            } catch (error) {
                console.error("Error fetching bins for PO row", error);
            } finally {
                setLoading(false);
            }
        };
        fetchBins();
    }, [sku, warehouse, poStatus, poUpdatedAt]);

    if (loading) return <div style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>Loading locations...</div>;

    if (bins.length === 0) {
        if (poStatus === 'closed') {
            return <div style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>No recent receiving locations found for this SKU in {warehouse}.</div>;
        }
        return <div style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>No bin locations found for this SKU in {warehouse}.</div>;
    }

    return (
        <div style={{ padding: '1rem 2rem', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)' }}>
            <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <PackageCheck size={16} /> {poStatus === 'closed' ? 'Received Locations' : 'Current Locations'} for {sku}
            </h4>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {bins.map((bin, i) => (
                    <div key={i} style={{ backgroundColor: 'white', padding: '0.5rem 1rem', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: '150px' }}>
                        <span style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.875rem' }}>{bin.location_name}</span>
                        <span style={{ color: 'var(--success)', fontSize: '0.8125rem', fontWeight: 500, backgroundColor: 'rgba(16, 185, 129, 0.1)', padding: '2px 8px', borderRadius: '12px' }}>Qty: {bin.quantity}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const POReceipts = () => {
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [activeTab, setActiveTab] = useState('overview');

    // Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClient, setSelectedClient] = useState('All');
    const [selectedWarehouse, setSelectedWarehouse] = useState('All');
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;

    useEffect(() => {
        const fetchReceipts = async () => {
            try {
                const res = await api.inventory.getPOReceipts().catch(() => null);
                if (res?.data && Array.isArray(res.data)) {
                    setReceipts(res.data);
                } else {
                    setReceipts([]);
                }
            } catch (error) {
                console.error("PO Receipts error", error);
            } finally {
                setLoading(false);
            }
        };

        fetchReceipts();

        const socketUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8009/api').replace('/api', '');
        const socket = io(socketUrl);

        socket.on('po_updated', (data) => {
            console.log("WebSocket: PO Updated", data);
            fetchReceipts();
        });

        socket.on('inventory_updated', (data) => {
            console.log("WebSocket: PO Inventory Updated", data);
            fetchReceipts();
        });

        return () => socket.disconnect();
    }, []);

    // Compute unique options for filters
    const uniqueWarehouses = useMemo(() => {
        const warehouses = new Set(receipts.map(r => r.warehouse_name).filter(Boolean));
        return ['All', ...Array.from(warehouses)];
    }, [receipts]);

    const uniqueStatuses = useMemo(() => {
        const statuses = new Set(receipts.map(r => r.po_status).filter(Boolean));
        return ['All', ...Array.from(statuses)];
    }, [receipts]);

    const uniqueClients = useMemo(() => {
        const clients = new Set(receipts.map(r => r.clientName).filter(Boolean));
        return ['All', ...Array.from(clients).sort()];
    }, [receipts]);

    const groupedReceipts = useMemo(() => {
        const groups = {};
        receipts.forEach(r => {
            const poKey = `${r.po_number || 'Unknown'}_${r.clientName || 'Unknown'}_${r.warehouse_name || 'Unknown'}`;
            if (!groups[poKey]) {
                groups[poKey] = {
                    poKey: poKey,
                    po_number: r.po_number || 'Unknown',
                    po_status: r.po_status,
                    warehouse_name: r.warehouse_name,
                    clientName: r.clientName,
                    expected_quantity: 0,
                    quantity_received: 0,
                    po_updated_at: r.updated_at,
                    skus: []
                };
            }
            r.expected_quantity = parseInt(r.expected_quantity || 0, 10);
            r.quantity_received = parseInt(r.quantity_received || 0, 10);
            r.total_on_hand = parseInt(r.total_on_hand || 0, 10);
            r.total_allocated = parseInt(r.total_allocated || 0, 10);
            groups[poKey].expected_quantity += r.expected_quantity;
            groups[poKey].quantity_received += r.quantity_received;
            groups[poKey].skus.push(r);
        });
        return Object.values(groups);
    }, [receipts]);

    const filteredReceipts = useMemo(() => {
        return groupedReceipts.filter(po => {
            const matchesSearch = po.po_number.toLowerCase().startsWith(searchTerm.toLowerCase()) ||
                po.skus.some(s =>
                    s.sku.toLowerCase().startsWith(searchTerm.toLowerCase()) ||
                    (s.product_name && s.product_name.toLowerCase().startsWith(searchTerm.toLowerCase()))
                );
            const matchesClient = selectedClient === 'All' || po.clientName === selectedClient;
            const matchesWarehouse = selectedWarehouse === 'All' || po.warehouse_name === selectedWarehouse;
            const matchesStatus = selectedStatus === 'All' || po.po_status === selectedStatus;

            return matchesSearch && matchesClient && matchesWarehouse && matchesStatus;
        });
    }, [groupedReceipts, searchTerm, selectedClient, selectedWarehouse, selectedStatus]);

    const { items: sortedReceipts, requestSort, sortConfig } = useSortableData(filteredReceipts);

    const totalPages = Math.ceil(sortedReceipts.length / itemsPerPage);
    const paginatedReceipts = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return sortedReceipts.slice(start, start + itemsPerPage);
    }, [sortedReceipts, currentPage]);

    const getSortIcon = (key) => {
        if (!sortConfig || sortConfig.key !== key) return <ArrowUpDown size={14} style={{ display: 'inline', marginLeft: '4px', opacity: 0.3 }} />;
        return sortConfig.direction === 'ascending' ? <ChevronUp size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--primary)' }} /> : <ChevronDown size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--primary)' }} />;
    };

    const toggleRow = (poNumber) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(poNumber)) {
            newExpanded.delete(poNumber);
        } else {
            newExpanded.add(poNumber);
        }
        setExpandedRows(newExpanded);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedClient, selectedWarehouse, selectedStatus]);

    if (loading) return <div className="p-8">Loading inbound PO receipts...</div>;

    return (
        <div>
            <div className="page-header" style={{ marginBottom: '1.5rem' }}>
                <div>
                    <h1>Purchase Order Receipts</h1>
                    <p>Track inbound purchase orders and received quantities against current global inventory.</p>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="tabs-container" style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '2px' }}>
                <button
                    className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        border: 'none',
                        background: 'transparent',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: activeTab === 'overview' ? 'var(--primary)' : 'var(--text-muted)',
                        borderBottom: activeTab === 'overview' ? '3px solid var(--primary)' : '3px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                    }}
                >
                    Monthly Overview
                </button>
                <button
                    className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                    onClick={() => setActiveTab('details')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        border: 'none',
                        background: 'transparent',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: activeTab === 'details' ? 'var(--primary)' : 'var(--text-muted)',
                        borderBottom: activeTab === 'details' ? '3px solid var(--primary)' : '3px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                    }}
                >
                    PO Records
                </button>
            </div>

            {activeTab === 'overview' && <POMetrics />}

            {activeTab === 'details' && (
                <>

                    <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem 1.5rem', marginTop: '2rem' }}>
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                            <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
                                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                <input
                                    type="text"
                                    placeholder="Search by PO number, SKU, or Product Name..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        width: '100%', padding: '0.6rem 1rem 0.6rem 2.5rem',
                                        borderRadius: '6px', border: '1px solid #e2e8f0',
                                        outline: 'none', fontFamily: 'inherit'
                                    }}
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '200px' }}>
                                <Filter size={18} color="var(--text-muted)" />
                                <select
                                    value={selectedClient}
                                    onChange={(e) => setSelectedClient(e.target.value)}
                                    style={{
                                        width: '100%', padding: '0.6rem 1rem',
                                        borderRadius: '6px', border: '1px solid #e2e8f0',
                                        outline: 'none', fontFamily: 'inherit',
                                        backgroundColor: 'white'
                                    }}
                                >
                                    {uniqueClients.map(c => (
                                        <option key={c} value={c}>{c === 'All' ? 'All Clients' : c}</option>
                                    ))}
                                </select>
                                <select
                                    value={selectedWarehouse}
                                    onChange={(e) => setSelectedWarehouse(e.target.value)}
                                    style={{
                                        width: '100%', padding: '0.6rem 1rem',
                                        borderRadius: '6px', border: '1px solid #e2e8f0',
                                        outline: 'none', fontFamily: 'inherit',
                                        backgroundColor: 'white'
                                    }}
                                >
                                    {uniqueWarehouses.map(wh => (
                                        <option key={wh} value={wh}>{wh === 'All' ? 'All Warehouses' : wh}</option>
                                    ))}
                                </select>
                                <select
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    style={{
                                        width: '100%', padding: '0.6rem 1rem',
                                        borderRadius: '6px', border: '1px solid #e2e8f0',
                                        outline: 'none', fontFamily: 'inherit',
                                        backgroundColor: 'white',
                                        marginLeft: '0.5rem'
                                    }}
                                >
                                    {uniqueStatuses.map(status => (
                                        <option key={status} value={status}>
                                            {status === 'All' ? 'All Statuses' : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="card table-container">
                        <table className="responsive-table" style={{ borderCollapse: 'collapse', borderSpacing: 0 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}></th>
                                    <th className="sortable" onClick={() => requestSort('po_number')}>PO Number {getSortIcon('po_number')}</th>
                                    <th className="sortable" onClick={() => requestSort('po_status')}>Status {getSortIcon('po_status')}</th>
                                    <th className="sortable" onClick={() => requestSort('warehouse_name')}>Warehouse {getSortIcon('warehouse_name')}</th>
                                    <th className="sortable" onClick={() => requestSort('clientName')}>Client {getSortIcon('clientName')}</th>
                                    <th style={{ textAlign: 'center' }}>SKU Lines</th>
                                    <th className="sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('expected_quantity')}>Total Expected {getSortIcon('expected_quantity')}</th>
                                    <th className="sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('quantity_received')}>Total Received {getSortIcon('quantity_received')}</th>
                                    <th style={{ textAlign: 'center' }}>Receive Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedReceipts.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--text-muted)' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                                                <PackageCheck size={48} style={{ opacity: 0.2 }} />
                                                <div>
                                                    <p style={{ fontWeight: 500, color: 'var(--text-main)', marginBottom: '0.25rem' }}>No purchase orders found</p>
                                                    <p style={{ fontSize: '0.875rem' }}>We couldn't find any inbound POs matching your current filters.</p>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedReceipts.map((receipt) => {
                                        const isFullyReceived = receipt.quantity_received >= receipt.expected_quantity && receipt.expected_quantity > 0;
                                        const isPartiallyReceived = receipt.quantity_received > 0 && receipt.quantity_received < receipt.expected_quantity;
                                        const isExpanded = expandedRows.has(receipt.poKey);

                                        let statusBadge;
                                        if (isFullyReceived) {
                                            statusBadge = <span className="badge success" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><PackageCheck size={14} /> Full</span>;
                                        } else if (isPartiallyReceived) {
                                            statusBadge = <span className="badge warning" style={{ backgroundColor: 'rgba(253, 206, 6, 0.15)', color: '#d97706', border: '1px solid rgba(253, 206, 6, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><AlertCircle size={14} /> Partial</span>;
                                        } else {
                                            statusBadge = <span className="badge" style={{ backgroundColor: '#f1f5f9', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>Pending</span>;
                                        }

                                        return (
                                            <React.Fragment key={receipt.poKey}>
                                                <tr
                                                    style={{ cursor: 'pointer', backgroundColor: isExpanded ? '#f8fafc' : 'transparent', transition: 'background-color 0.2s', borderBottom: isExpanded ? 'none' : '1px solid #e2e8f0' }}
                                                    onClick={() => toggleRow(receipt.poKey)}
                                                >
                                                    <td style={{ width: '40px', padding: '1rem', textAlign: 'center' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: isExpanded ? 'var(--primary)' : 'var(--text-muted)' }}>
                                                            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                                        </div>
                                                    </td>
                                                    <td data-label="PO Number" style={{ fontWeight: 600, color: 'var(--primary)', padding: '1rem' }} title={receipt.po_number}>{receipt.po_number}</td>
                                                    <td data-label="Status" style={{ padding: '1rem' }}>
                                                        <span style={{
                                                            textTransform: 'capitalize',
                                                            fontSize: '0.8125rem',
                                                            color: receipt.po_status === 'closed' ? 'var(--success)' : 'var(--text-main)'
                                                        }}>{receipt.po_status?.replace('_', ' ')}</span>
                                                    </td>
                                                    <td data-label="Warehouse" style={{ color: 'var(--text-main)', padding: '1rem' }} title={receipt.warehouse_name}>{receipt.warehouse_name || 'N/A'}</td>
                                                    <td data-label="Client" style={{ fontWeight: 600, color: 'var(--primary)', padding: '1rem' }} title={receipt.clientName}>{receipt.clientName}</td>
                                                    <td data-label="SKU Lines" style={{ textAlign: 'center', padding: '1rem' }}>{receipt.skus.length}</td>
                                                    <td data-label="Expected Qty" style={{ textAlign: 'right', padding: '1rem' }}>{receipt.expected_quantity?.toLocaleString() || 0}</td>
                                                    <td data-label="Received Qty" style={{ textAlign: 'right', fontWeight: 'bold', padding: '1rem' }}>{receipt.quantity_received?.toLocaleString() || 0}</td>
                                                    <td data-label="Receive Status" style={{ textAlign: 'center', padding: '1rem' }}>{statusBadge}</td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr style={{ backgroundColor: '#f8fafc' }}>
                                                        <td colSpan="9" style={{ padding: '0 0 1rem 0', borderBottom: '1px solid #e2e8f0' }}>
                                                            <div style={{ marginLeft: '40px', marginRight: '1rem', padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                                                                    <thead>
                                                                        <tr style={{ borderBottom: '1px solid #e2e8f0', color: 'var(--text-muted)' }}>
                                                                            <th style={{ padding: '0.5rem', textAlign: 'left' }}>SKU</th>
                                                                            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Product Name</th>
                                                                            <th style={{ padding: '0.5rem', textAlign: 'right' }}>Expected</th>
                                                                            <th style={{ padding: '0.5rem', textAlign: 'right' }}>Received</th>
                                                                            <th style={{ padding: '0.5rem', textAlign: 'right' }}>Network On Hand</th>
                                                                            <th style={{ padding: '0.5rem', textAlign: 'right' }}>Network Allocated</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {receipt.skus.map((skuLine, skuIdx) => (
                                                                            <React.Fragment key={skuIdx}>
                                                                                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                                    <td style={{ padding: '0.75rem 0.5rem', fontFamily: 'monospace', fontWeight: 600 }}>{skuLine.sku}</td>
                                                                                    <td style={{ padding: '0.75rem 0.5rem' }}>{skuLine.product_name || 'Unknown'}</td>
                                                                                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{skuLine.expected_quantity?.toLocaleString() || 0}</td>
                                                                                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>{skuLine.quantity_received?.toLocaleString() || 0}</td>
                                                                                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: skuLine.total_on_hand > 0 ? 'inherit' : 'var(--danger)' }}>{skuLine.total_on_hand?.toLocaleString() || 0}</td>
                                                                                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{skuLine.total_allocated?.toLocaleString() || 0}</td>
                                                                                </tr>
                                                                                {skuLine.quantity_received > 0 && (
                                                                                    <tr>
                                                                                        <td colSpan="6" style={{ padding: 0 }}>
                                                                                            <div style={{ marginLeft: '1rem', borderLeft: '2px solid #e2e8f0', marginBottom: '0.5rem' }}>
                                                                                                <POBinDetails 
                                                                                                    sku={skuLine.sku} 
                                                                                                    warehouse={skuLine.warehouse_name} 
                                                                                                    poStatus={receipt.po_status}
                                                                                                    poUpdatedAt={receipt.po_updated_at}
                                                                                                />
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>

                        {sortedReceipts.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px' }}>
                                <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedReceipts.length)} of {sortedReceipts.length} entries
                                </div>
                                <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} />
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default POReceipts;
